import {
  AccessCode,
  AppUser,
  BulkJob,
  BulkUploadDraft,
  ControllerVendor,
  ControllerVendorDraft,
  DashboardSnapshot,
  GroupAccessCode,
  GroupCodeDraft,
  HospitalityEvent,
  HospitalityEventDraft,
  Location,
  ManualCodeDraft,
  PmsVendor,
  PmsVendorDraft,
  Space,
  Tenant,
  TenantDraft
} from '../models/hospitality';
import { ApiResponse, DeletedEntityData } from '../models/api-response';
import {
  CreateLocationRequest,
  CreateControllerVendorRequest,
  CreatePmsVendorRequest,
  CreateUserRequest,
  CreateUnitRequest,
  BulkCreateLocationsRequest,
  BulkCreateUnitsRequest,
  UpdateLocationRequest,
  UpdateControllerVendorRequest,
  UpdatePmsVendorRequest,
  UpdateUnitRequest,
  UpdateUserRequest
} from '../models/dashboard-api';
import {
  ActiveCodesQuery,
  ArchivedCodesQuery,
  BulkJobsQuery,
  ControllerVendorsQuery,
  EventNetworksQuery,
  EventsQuery,
  LocationsQuery,
  PagedControllerVendorResult,
  PagedEventNetworkResult,
  PagedEventResult,
  PagedAccessCodeResult,
  PagedBulkJobResult,
  PagedLocationResult,
  PagedPmsVendorResult,
  PagedSpaceResult,
  PagedTenantResult,
  PagedUserResult,
  PmsVendorsQuery,
  SpaceListQuery,
  TenantsQuery,
  UsersQuery
} from '../models/hospitality-tables';

export abstract class HospitalityApiService {
  abstract loadDashboardSnapshot(tenantId: string): Promise<ApiResponse<DashboardSnapshot>>;

  replaceSnapshot(_snapshot: DashboardSnapshot): void {}

  abstract listLocationSpaces(query: SpaceListQuery): Promise<ApiResponse<PagedSpaceResult>>;
  abstract getActiveSpaceCode(
    tenantId: string,
    locationId: string,
    spaceId: string
  ): Promise<ApiResponse<AccessCode>>;
  abstract getActiveSpaceNetworkQr(
    tenantId: string,
    locationId: string,
    spaceId: string
  ): Promise<ApiResponse<{ ssid: string; passphrase: string; qrPayload: string }>>;
  abstract listActiveLocationCodes(query: ActiveCodesQuery): Promise<ApiResponse<PagedAccessCodeResult>>;
  abstract listArchivedLocationCodes(
    query: ArchivedCodesQuery
  ): Promise<ApiResponse<PagedAccessCodeResult>>;
  abstract listLocationEvents(query: EventsQuery): Promise<ApiResponse<PagedEventResult>>;
  abstract listLocationEventNetworks(
    query: EventNetworksQuery
  ): Promise<ApiResponse<PagedEventNetworkResult>>;
  abstract listLocationBulkJobs(query: BulkJobsQuery): Promise<ApiResponse<PagedBulkJobResult>>;
  abstract listTenants(query: TenantsQuery): Promise<ApiResponse<PagedTenantResult>>;
  abstract listUsers(query: UsersQuery): Promise<ApiResponse<PagedUserResult>>;
  abstract listLocations(query: LocationsQuery): Promise<ApiResponse<PagedLocationResult>>;
  abstract listControllerVendors(
    query: ControllerVendorsQuery
  ): Promise<ApiResponse<PagedControllerVendorResult>>;
  abstract listPmsVendors(query: PmsVendorsQuery): Promise<ApiResponse<PagedPmsVendorResult>>;
  abstract listControllerVendorOptions(): Promise<ApiResponse<ControllerVendor[]>>;
  abstract listPmsVendorOptions(): Promise<ApiResponse<PmsVendor[]>>;
  abstract getControllerVendor(vendorId: string): Promise<ApiResponse<ControllerVendor>>;
  abstract getPmsVendor(vendorId: string): Promise<ApiResponse<PmsVendor>>;

  abstract createTenant(draft: TenantDraft): Promise<ApiResponse<Tenant>>;
  abstract updateTenant(tenantId: string, draft: TenantDraft): Promise<ApiResponse<Tenant>>;
  abstract deleteTenant(tenantId: string): Promise<ApiResponse<DeletedEntityData>>;
  abstract createControllerVendor(
    draft: CreateControllerVendorRequest | ControllerVendorDraft
  ): Promise<ApiResponse<ControllerVendor>>;
  abstract updateControllerVendor(
    vendorId: string,
    draft: UpdateControllerVendorRequest | ControllerVendorDraft
  ): Promise<ApiResponse<ControllerVendor>>;
  abstract deleteControllerVendor(vendorId: string): Promise<ApiResponse<DeletedEntityData>>;
  abstract createPmsVendor(
    draft: CreatePmsVendorRequest | PmsVendorDraft
  ): Promise<ApiResponse<PmsVendor>>;
  abstract updatePmsVendor(
    vendorId: string,
    draft: UpdatePmsVendorRequest | PmsVendorDraft
  ): Promise<ApiResponse<PmsVendor>>;
  abstract deletePmsVendor(vendorId: string): Promise<ApiResponse<DeletedEntityData>>;

  abstract createLocation(
    tenantId: string,
    draft: CreateLocationRequest
  ): Promise<ApiResponse<Location>>;
  abstract updateLocation(
    tenantId: string,
    locationId: string,
    draft: UpdateLocationRequest
  ): Promise<ApiResponse<Location>>;
  abstract deleteLocation(
    tenantId: string,
    locationId: string
  ): Promise<ApiResponse<DeletedEntityData>>;
  abstract bulkCreateLocations(
    tenantId: string,
    request: BulkCreateLocationsRequest
  ): Promise<ApiResponse<{ created: number; failed: number; items: Location[] }>>;

  abstract createUser(draft: CreateUserRequest): Promise<ApiResponse<AppUser>>;
  abstract updateUser(userId: string, draft: UpdateUserRequest): Promise<ApiResponse<AppUser>>;
  abstract deleteUser(userId: string): Promise<ApiResponse<DeletedEntityData>>;

  abstract createUnit(
    tenantId: string,
    locationId: string,
    draft: CreateUnitRequest
  ): Promise<ApiResponse<Space>>;
  abstract updateUnit(
    tenantId: string,
    locationId: string,
    spaceId: string,
    draft: UpdateUnitRequest
  ): Promise<ApiResponse<Space>>;
  abstract deleteUnit(
    tenantId: string,
    locationId: string,
    spaceId: string
  ): Promise<ApiResponse<DeletedEntityData>>;
  abstract bulkCreateUnits(
    tenantId: string,
    locationId: string,
    request: BulkCreateUnitsRequest
  ): Promise<ApiResponse<{ created: number; failed: number; items: Space[] }>>;

  abstract createManualCode(
    tenantId: string,
    locationId: string,
    draft: Omit<ManualCodeDraft, 'locationId'>,
    spaces: Space[]
  ): Promise<ApiResponse<AccessCode>>;
  abstract extendCode(
    tenantId: string,
    locationId: string,
    codeId: string,
    hours: number
  ): Promise<ApiResponse<AccessCode>>;
  abstract revokeCode(
    tenantId: string,
    locationId: string,
    codeId: string
  ): Promise<ApiResponse<AccessCode>>;
  abstract sendCodeEmail(
    tenantId: string,
    locationId: string,
    codeId: string
  ): Promise<ApiResponse<{ codeId?: string; channel: 'email'; deliveredAt: string }>>;
  abstract createEvent(
    tenantId: string,
    locationId: string,
    draft: HospitalityEventDraft
  ): Promise<ApiResponse<HospitalityEvent>>;
  abstract updateEvent(
    tenantId: string,
    locationId: string,
    eventId: string,
    draft: HospitalityEventDraft
  ): Promise<ApiResponse<HospitalityEvent>>;
  abstract disableEvent(
    tenantId: string,
    locationId: string,
    eventId: string
  ): Promise<ApiResponse<HospitalityEvent>>;
  abstract sendEventEmail(
    tenantId: string,
    locationId: string,
    eventId: string
  ): Promise<ApiResponse<{ eventId?: string; channel: 'email'; deliveredAt: string }>>;
  abstract createGroupCode(
    tenantId: string,
    locationId: string,
    spaceId: string,
    draft: GroupCodeDraft
  ): Promise<ApiResponse<GroupAccessCode>>;

  abstract createBulkJob(
    locationId: string,
    draft: Omit<BulkUploadDraft, 'locationId'>,
    spaces: Space[]
  ): Promise<ApiResponse<{ job: BulkJob; codes: AccessCode[] }>>;

  abstract generateQrCode(payload: string): Promise<string>;
}
